import React from 'react';
import { Code, Server, BarChart3, CheckCircle, Eye, Box, Zap, Target } from 'lucide-react';

const MetricCard = ({ title, value, icon, color, chartType }) => {
  const colorClasses = {
    cyan: 'text-cyan-accent',
    green: 'text-green-accent',
    orange: 'text-orange-accent',
    yellow: 'text-yellow-accent',
    purple: 'text-purple-accent'
  };

  const iconComponents = {
    code: <Code size={60} strokeWidth={1.5} />,
    server: <Server size={60} strokeWidth={1.5} />,
    chart: <BarChart3 size={60} strokeWidth={1.5} />,
    health: <CheckCircle size={60} strokeWidth={1.5} />,
    monitor: <Eye size={60} strokeWidth={1.5} />,
    box: <Box size={60} strokeWidth={1.5} />,
    nodes: <Zap size={60} strokeWidth={1.5} />,
    target: <Target size={60} strokeWidth={1.5} />
  };

  const renderChart = () => {
    if (chartType === 'line') {
      return (
        <svg className="w-full h-8" viewBox="0 0 200 32">
          <defs>
            <linearGradient id={`lineGlow-${title}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
              <stop offset="50%" stopColor="currentColor" stopOpacity="0.8" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          <path
            d="M 0,22 Q 25,18 50,20 T 100,16 T 150,14 T 200,12"
            fill="none"
            stroke={`url(#lineGlow-${title})`}
            strokeWidth="2"
            className={colorClasses[color]}
            filter="drop-shadow(0 0 6px currentColor) drop-shadow(0 0 12px currentColor)"
          />
        </svg>
      );
    }
    
    if (chartType === 'bar') {
      const bars = [35, 45, 38, 50, 42, 55, 48, 60];
      return (
        <div className="flex items-end justify-between h-8 gap-0.5">
          {bars.map((height, idx) => (
            <div
              key={idx}
              className={`flex-1 ${colorClasses[color]} opacity-70 rounded-t`}
              style={{ 
                height: `${height}%`,
                backgroundColor: 'currentColor',
                boxShadow: `0 0 8px currentColor, 0 0 16px currentColor`
              }}
            />
          ))}
        </div>
      );
    }

    if (chartType === 'area') {
      return (
        <svg className="w-full h-8" viewBox="0 0 200 32" preserveAspectRatio="none">
          <defs>
            <linearGradient id={`gradient-${title}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.5" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M 0,22 Q 25,18 50,20 T 100,16 T 150,14 T 200,12 L 200,32 L 0,32 Z"
            fill={`url(#gradient-${title})`}
            stroke="currentColor"
            strokeWidth="1.5"
            className={colorClasses[color]}
            filter="drop-shadow(0 0 6px currentColor) drop-shadow(0 0 12px currentColor)"
          />
        </svg>
      );
    }

    if (chartType === 'circle') {
      const percentage = parseFloat(value);
      const circumference = 2 * Math.PI * 14;
      const offset = circumference - (percentage / 100) * circumference;
      
      return (
        <div className="flex items-center justify-center h-8">
          <svg className="w-10 h-10 transform -rotate-90">
            <circle
              cx="20"
              cy="20"
              r="14"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              className="text-dark-border opacity-40"
            />
            <circle
              cx="20"
              cy="20"
              r="14"
              stroke="currentColor"
              strokeWidth="2.5"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className={colorClasses[color]}
              strokeLinecap="round"
              style={{
                filter: `drop-shadow(0 0 6px currentColor) drop-shadow(0 0 12px currentColor)`
              }}
            />
          </svg>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="relative bg-dark-card border border-dark-border rounded-lg p-3 overflow-hidden group hover:border-current transition-all duration-300"
         style={{
           background: '#141414',
           boxShadow: `0 0 1px ${colorClasses[color].replace('text-', '')}30`
         }}>
      
      {/* Background Icon - Shadowed/Watermark */}
      <div className={`absolute -right-3 -top-3 opacity-[0.08] ${colorClasses[color]} pointer-events-none transform group-hover:scale-105 group-hover:opacity-[0.15] transition-all duration-300`}
           style={{
             filter: `drop-shadow(0 0 25px currentColor) drop-shadow(0 0 50px currentColor)`
           }}>
        {iconComponents[icon]}
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Small Icon Badge */}
        <div className={`${colorClasses[color]} w-7 h-7 rounded flex items-center justify-center mb-2`}
             style={{
               backgroundColor: 'currentColor',
               opacity: 0.15,
               boxShadow: `0 0 20px currentColor, 0 0 40px currentColor`
             }}>
          <div className={colorClasses[color]} style={{ filter: 'brightness(2)' }}>
            {React.cloneElement(iconComponents[icon], { size: 14, strokeWidth: 2.5 })}
          </div>
        </div>

        {/* Title */}
        <h3 className="text-[10px] text-gray-500 mb-1 font-medium tracking-widest uppercase leading-tight"
            style={{ textShadow: '0 0 5px rgba(255,255,255,0.1)' }}>
          {title}
        </h3>

        {/* Value */}
        <div className={`text-xl font-bold ${colorClasses[color]} mb-2 tracking-tight leading-none`}
             style={{
               textShadow: `0 0 20px currentColor, 0 0 40px currentColor, 0 0 60px currentColor`,
               filter: 'brightness(1.2)'
             }}>
          {value}
        </div>

        {/* Chart */}
        <div className="mt-1.5">
          {renderChart()}
        </div>
      </div>

      {/* Neon glow border effect */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${colorClasses[color]} rounded-lg`}
           style={{
             boxShadow: `inset 0 0 20px currentColor, 0 0 20px currentColor`
           }} />
      
      {/* Scan line effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white to-transparent opacity-[0.05] animate-scan" />
      </div>
    </div>
  );
};

export default MetricCard;

